import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  X, 
  Plus, 
  Globe, 
  Palette, 
  Phone, 
  Mail, 
  MapPin, 
  Save,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

interface DomainManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: number;
  tenantName: string;
  onDomainAdded: () => void;
}

interface DomainFormData {
  domain_name: string;
  client_name: string;
  is_primary: boolean;
  primary_color: string;
  secondary_color: string;
  logo_url: string;
  icon_url: string;
  bg_url: string;
  phone: string;
  email: string;
  address: string;
}

const initialFormData: DomainFormData = {
  domain_name: '',
  client_name: '',
  is_primary: false,
  primary_color: '',
  secondary_color: '',
  logo_url: '',
  icon_url: '',
  bg_url: '',
  phone: '',
  email: '',
  address: ''
};

export const DomainManagementModal: React.FC<DomainManagementModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  onDomainAdded
}) => {
  const [formData, setFormData] = useState<DomainFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Réinitialiser le formulaire à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof DomainFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.domain_name.trim()) {
      setError('Le nom de domaine est requis');
      return false;
    }
    
    if (!formData.client_name.trim()) {
      setError('Le nom du client est requis');
      return false;
    }

    // Validation basique du domaine
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(formData.domain_name)) {
      setError('Format de domaine invalide');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      // Construire la configuration du domaine
      const domainConfig: any = {
        name: formData.client_name
      };

      // Ajouter les couleurs si spécifiées
      if (formData.primary_color || formData.secondary_color) {
        domainConfig.colors = {};
        if (formData.primary_color) domainConfig.colors.primary = formData.primary_color;
        if (formData.secondary_color) domainConfig.colors.secondary = formData.secondary_color;
      }

      // Ajouter les médias si spécifiés
      if (formData.logo_url) domainConfig.logo = formData.logo_url;
      if (formData.icon_url) domainConfig.icon = formData.icon_url;
      if (formData.bg_url) domainConfig.bg = formData.bg_url;

      // Ajouter les informations de contact si spécifiées
      if (formData.phone) domainConfig.phone = formData.phone;
      if (formData.email) domainConfig.email = formData.email;
      if (formData.address) domainConfig.address = formData.address;

      // Insérer le domaine via RPC pour bypasser RLS
      const { data: domainId, error: domainError } = await supabase
        .rpc('insert_tenant_domain', {
          tenant_id_param: tenantId,
          domain_name_param: formData.domain_name.toLowerCase().trim(),
          is_primary_param: formData.is_primary,
          config_param: domainConfig
        });

      if (domainError) throw domainError;

      // Mettre à jour les domain_overrides du tenant parent
      const { data: tenantData, error: tenantFetchError } = await supabase
        .from('tenants')
        .select('config')
        .eq('id', tenantId)
        .single();

      if (tenantFetchError) throw tenantFetchError;

      const currentConfig = tenantData.config || {};
      const domainOverrides = currentConfig.domain_overrides || {};
      domainOverrides[formData.domain_name.toLowerCase().trim()] = domainConfig;

      const { error: tenantUpdateError } = await supabase
        .from('tenants')
        .update({
          config: {
            ...currentConfig,
            domain_overrides: domainOverrides
          }
        })
        .eq('id', tenantId);

      if (tenantUpdateError) throw tenantUpdateError;

      setSuccess(`Domaine ${formData.domain_name} ajouté avec succès !`);
      
      // Notifier le parent
      onDomainAdded();
      
      // Fermer après un délai
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err: any) {
      setError(`Erreur lors de l'ajout du domaine: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Plus className="w-5 h-5 mr-2" />
              Ajouter un Domaine
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Tenant: {tenantName} (ID: {tenantId})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <span className="text-green-700">{success}</span>
          </div>
        )}

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations de base */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Globe className="w-5 h-5 mr-2" />
              Informations de Base
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de Domaine *
                </label>
                <input
                  type="text"
                  value={formData.domain_name}
                  onChange={(e) => handleInputChange('domain_name', e.target.value)}
                  placeholder="exemple.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du Client *
                </label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => handleInputChange('client_name', e.target.value)}
                  placeholder="Nom de l'entreprise"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_primary"
                checked={formData.is_primary}
                onChange={(e) => handleInputChange('is_primary', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_primary" className="ml-2 block text-sm text-gray-900">
                Domaine primaire pour ce tenant
              </label>
            </div>
          </div>

          {/* Personnalisation visuelle */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Palette className="w-5 h-5 mr-2" />
              Personnalisation Visuelle
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur Primaire
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => handleInputChange('primary_color', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => handleInputChange('primary_color', e.target.value)}
                    placeholder="#ff0000"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur Secondaire
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.secondary_color}
                    onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                    placeholder="#00ff00"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Logo
                </label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Icône
                </label>
                <input
                  type="url"
                  value={formData.icon_url}
                  onChange={(e) => handleInputChange('icon_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Background
                </label>
                <input
                  type="url"
                  value={formData.bg_url}
                  onChange={(e) => handleInputChange('bg_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Informations de contact */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Informations de Contact
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="(+242) 06 000 0000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="contact@exemple.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Adresse complète"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {loading ? 'Ajout...' : 'Ajouter le Domaine'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
